import { DateTime, Interval, Duration } from "luxon";

import constants from "./constants";

//scrolTo(0, 0) doesn't work
export const scrollTop = () => {
  document.getElementById("widgetTop").scrollIntoView();
};

const now = DateTime.now();

export const generateAvailableMonths = (branchInfo) => {
  const today = DateTime.fromObject({ year: now.year, month: now.month, day: now.day });

  // const today = DateTime.fromObject({ year: now.year, month: now.month, day: now.day });
  //Interval from today to the max lead time from today. Includes the start DateTime object and the end DateTime object
  const calendarSpanInterval = Interval.fromDateTimes(
    today,
    now.plus(Duration.fromObject({ days: branchInfo.maxLeadTime }))
  );
  //Split the interval into smaller intervals of 1 month each
  const calendarSpanIntervals = calendarSpanInterval.splitBy(Duration.fromObject({ months: 1 }));
  //DateTime object of when the last interval ends
  const lastIntervalEndMonth = calendarSpanIntervals[calendarSpanIntervals.length - 1].end;
  //Adds the start DateTime of each interval to the selectable months
  const availableMonths = [];

  const branchClose = DateTime.fromSQL(branchInfo.dayStart + " " + branchInfo.timeZone);
  const dayEnd = today.set({
    hour: branchClose.hour,
    minute: branchClose.minute,
    second: 0,
    milliseconds: 0,
  });

  const isLastDay = today.day === today.endOf("month").day;

  calendarSpanIntervals.forEach((interval) => {
    if (!isLastDay || (isLastDay && interval.start.month === today.month && dayEnd >= now)) {
      availableMonths.push(DateTime.fromObject({ year: interval.start.year, month: interval.start.month }));
    }
  });

  /*Most of the times, intervals dos not start and end on the same month. This checks if the last interval ends on a different month.
  If that is the case, adds the end month of the last interval to the selectable months (We are adding only the start month of each interval)*/
  if (availableMonths.length < 1 || availableMonths[availableMonths.length - 1].month !== lastIntervalEndMonth.month) {
    availableMonths.push(DateTime.fromObject({ year: lastIntervalEndMonth.year, month: lastIntervalEndMonth.month }));
  }

  return availableMonths;
};

/**
 * Check if days are not fully blocked, within configured params, and check meetings and blocked times for each day of all available months
 * @param {*} availableMonths
 * @param {*} branchInfo
 * @param {*} meetings
 * @param {*} blockedTimes
 * @returns
 */
export const generateAvailableDays = (loading, availableMonths, branchInfo, meetings, blockedTimes) => {
  if (loading) {
    return;
  }

  const availableDays = {};
  const branchOpen = DateTime.fromSQL(branchInfo.dayStart + " " + branchInfo.timeZone);
  const branchClose = DateTime.fromSQL(branchInfo.dayStart + " " + branchInfo.timeZone);

  availableMonths.forEach((monthDateTime) => {
    availableDays[monthDateTime.month] = {};
    const daysInMonth = monthDateTime.daysInMonth;

    for (let i = 1; i <= daysInMonth; i++) {
      const dayStart = monthDateTime.set({
        day: i,
        hour: branchOpen.hour,
        minute: branchOpen.minute,
        second: 0,
        milliseconds: 0,
      });

      const dayEnd = monthDateTime.set({
        day: i,
        hour: branchClose.hour,
        minute: branchClose.minute,
        second: 0,
        milliseconds: 0,
      });

      const weekday = dayStart.weekdayShort;
      const weekdayNumber = constants.WEEKDAYS[weekday.toUpperCase()];
      const beforeToday = monthDateTime.month === now.month && dayStart.day < now.day;

      //Check if day is within the operational days, if the day is after the minimum lead time, and if day is before max lead time
      if (
        weekdayNumber >= branchInfo.weekStart &&
        weekdayNumber <= branchInfo.weekEnd &&
        !beforeToday &&
        dayStart <= now.plus(Duration.fromObject({ days: branchInfo.maxLeadTime })) &&
        dayEnd >= now.plus(Duration.fromObject({ hours: branchInfo.minLeadTime }))
      ) {
        const dayDate = dayStart.toFormat("yyyy-MM-dd");
        const dayBlockedTimes = [];
        const dayMeetings = [];
        let availableDay = true;

        if (availableDay) {
          //Check blocked times for the day
          blockedTimes.forEach((blockedTime) => {
            const blockedTimeDate = blockedTime.start.split(" ")[0];

            if (dayDate === blockedTimeDate) {
              const blockedTimeStart = DateTime.fromSQL(blockedTime.start);
              const blockedTimeEnd = DateTime.fromSQL(blockedTime.end);

              //If the whole days is blocked
              if (dayStart >= blockedTimeStart && dayEnd <= blockedTimeEnd) {
                return (availableDay = false);
              } else {
                dayBlockedTimes.push(blockedTime);
              }
            }
          });

          //Check the meetings for the day
          meetings.forEach((meeting) => {
            const meetingTimeDate = meeting.datetime.split(" ")[0];

            if (dayDate === meetingTimeDate) {
              dayMeetings.push(meeting);
            }
          });

          //Assign the information for the day if the day is available
          if (availableDay) {
            availableDays[monthDateTime.month][dayStart] = {
              date: dayStart,
              nextOpenDate: null,
              blockedTimes: dayBlockedTimes,
              meetings: dayMeetings,
            };
          } else {
            availableDays[monthDateTime.month][dayStart] = null;
          }
        }
      } else {
        availableDays[monthDateTime.month][dayStart] = null;
      }
    }
  });

  return availableDays;
};

const isTimeSlotAvailable = (timeStart, timeEnd, calendarInfo, timeSlotDate) => {
  const slotsAllocationDuration = calendarInfo.slotsAllocationDuration;

  //Must block at least the meeting allocation time from the blocked start time
  const minBlockedTime = timeStart.plus(slotsAllocationDuration);

  //To check if the start is interfering with a previous slot allocation time
  const intervalBefore = timeStart.minus(slotsAllocationDuration);

  //Use the minimum allocation if it is greater than the blocked end time
  const blockedEnd = timeEnd >= minBlockedTime ? timeEnd : minBlockedTime;

  // Determine how we check if the interval before the blocked start time is earlier than the slot time
  const intervalBeforeLessThanSlotTime =
    calendarInfo.slotsIntervalDuration === slotsAllocationDuration
      ? intervalBefore <= timeSlotDate
      : intervalBefore < timeSlotDate;

  //Is the slot time within the blocked time interval?
  return !intervalBeforeLessThanSlotTime || blockedEnd < timeSlotDate;
};

//Check time slots agains blocked times and booked meetings
const checkTimeSlots = (currentDay, blockedTimes, meetings, timeSlots, calendarInfo, availableTimes) => {
  availableTimes = availableTimes || { am: true, pm: true };
  const morningMinutes = 719;
  const notWithinAvailableTimes = [];
  let dayStart = calendarInfo.dayStart;
  let dayEnd = calendarInfo.dayEnd;

  Object.keys(timeSlots).forEach((timeSlot) => {
    //Leave loop if entire day has been blocked or if the slot is blocked
    if (!timeSlots || !timeSlots[timeSlot]) {
      return;
    }

    const timeSlotDate = timeSlots[timeSlot]; //Slot date object
    const currentTimeInMinutes = timeSlotDate.hour * 60 + timeSlotDate.minute;

    //If time slot is not within the selected time period (AM/PM), add time slot to the list of unnavailable times
    if (availableTimes.am && !availableTimes.pm && currentTimeInMinutes > morningMinutes) {
      notWithinAvailableTimes.push(timeSlot);
    }

    if (availableTimes.pm && !availableTimes.am && currentTimeInMinutes <= morningMinutes) {
      notWithinAvailableTimes.push(timeSlot);
    }

    //Check each time slot against the blocked times
    blockedTimes.forEach((blockedTime) => {
      if (timeSlots) {
        let blockedTimeStart = DateTime.fromSQL(blockedTime.start + " " + calendarInfo.timeZone);
        let blockedTimeEnd = DateTime.fromSQL(blockedTime.end + " " + calendarInfo.timeZone);
        dayStart = dayStart.set({ month: blockedTimeStart.month, day: blockedTimeStart.day });
        dayEnd = dayEnd.set({ month: blockedTimeStart.month, day: blockedTimeStart.day });

        /*Next two conditionals ensures the blockedTimeInterval will not be greater than the branchOpenInterval if the
        start or end times of the blocked time are outside branch working hours*/
        if (blockedTimeStart < dayStart) {
          blockedTimeStart = blockedTimeStart.set({ hour: dayStart.hour, minute: dayStart.minute });
        }

        if (blockedTimeEnd > dayEnd) {
          blockedTimeEnd = blockedTimeEnd.set({ hour: dayEnd.hour, minute: dayEnd.minute });
        }

        const blockedTimeInterval = Interval.fromDateTimes(blockedTimeStart, blockedTimeEnd).length("minutes");

        //If entire day is blocked, remove time slots entirely
        if (currentDay === blockedTimeStart.toISODate() && blockedTimeInterval >= calendarInfo.branchOpenInterval) {
          return (timeSlots = null);
        }

        if (!isTimeSlotAvailable(blockedTimeStart, blockedTimeEnd, calendarInfo, timeSlotDate)) {
          timeSlots[timeSlot] = null;
        }
      }
    });

    //Check each time slot against existing meetings and against the allowed number of concurrent meetings
    const concurrentMeetings = {};

    meetings.forEach((meeting) => {
      const lawArea = JSON.parse(meeting.additionalInfo).lawArea;

      //Skip if no slots or if it is a real estate inquiry (Won't burn the slot)
      if (timeSlots && lawArea !== "Real Estate") {
        const meetingDateTime = DateTime.fromSQL(meeting.datetime + " " + calendarInfo.timeZone);

        if (!isTimeSlotAvailable(meetingDateTime, meetingDateTime, calendarInfo, timeSlotDate)) {
          if (!concurrentMeetings[timeSlot]) {
            concurrentMeetings[timeSlot] = 1;
          } else {
            concurrentMeetings[timeSlot]++;
          }

          if (concurrentMeetings[timeSlot] >= calendarInfo.concurrentMeetings) {
            timeSlots[timeSlot] = null;
          }
        }
      }
    });
  });

  //If all time slots are either blocked or not available in the selected time period (AM/PM), block entire day
  if (timeSlots) {
    let dayNotAvailable = true;

    Object.keys(timeSlots).forEach((timeSlot) => {
      if (timeSlots[timeSlot] && !notWithinAvailableTimes.includes(timeSlot)) {
        return (dayNotAvailable = false);
      }
    });

    if (dayNotAvailable) {
      timeSlots = null;
    }
  }

  return timeSlots;
};

/**
 * Generate the time slots for the selected day
 * @param {Object} selectedDateInfo Object containing the date, next available date, blocked times, and meetings
 * @param {Object} calendarInfo Object containing branch and calendar configuration
 * @param {Object} availableTimes Object with the AM/PM preferences (Not currently in use)
 * @returns Object with all available time slots for the day
 */
export const generateTimeSlots = (selectedDateInfo, calendarInfo, availableTimes) => {
  const selectedDate = selectedDateInfo?.date;
  const nextOpenDate = selectedDateInfo?.nextOpenDate;
  const branchTimeZone = calendarInfo.timeZone;
  const todaysDayEnd = now.set(
    { hour: calendarInfo.dayEnd.hour, minute: calendarInfo.dayEnd.minute },
    { zone: branchTimeZone }
  );

  const dayStart = selectedDate
    ? selectedDate.set({
        hour: calendarInfo.dayStart.hour,
        minute: calendarInfo.dayStart.minute,
        second: 0,
        millisecond: 0,
      })
    : now.set({
        hour: calendarInfo.dayStart.hour,
        minute: calendarInfo.dayStart.minute,
        second: 0,
        millisecond: 0,
      });

  const dayEnd = selectedDate
    ? selectedDate.set({
        hour: calendarInfo.dayEnd.hour,
        minute: calendarInfo.dayEnd.minute,
        second: 0,
        millisecond: 0,
      })
    : todaysDayEnd;

  let timeSlots = {};
  let currentTimeSlot = dayStart;

  //Generate and check time slots according to number of slots
  let slotsCount = 0;
  while (slotsCount <= calendarInfo.numerOfSlots) {
    slotsCount++;
    let slotEnabled = selectedDate ? true : false;

    //Will only proceed if there is a selected date (if not it will be disabled) and if the slot is still enabled
    //In case all slots are disabled this will act like dummy slots for when the date is still not selected
    if (selectedDate && slotEnabled) {
      //Check if this time slot is earlier than the minimum lead hours or after maximum lead days
      const lessThanMinLeadHours = currentTimeSlot < calendarInfo.minLeadTimeDuration;
      const greaterThanMaxLeadDays = currentTimeSlot > calendarInfo.maxLeadTimeDuration;

      if (lessThanMinLeadHours || greaterThanMaxLeadDays) {
        slotEnabled = false;
      }

      //Is now + lead time before the slot's branch opening time?
      if (
        slotEnabled &&
        calendarInfo.minLeadTimeDuration < dayStart &&
        currentTimeSlot.diff(nextOpenDate, "hours").hours < 24
      ) {
        const timeLeftUntilClose = todaysDayEnd.diffNow("hours").hours;
        const slotOpenDiff = currentTimeSlot.diff(dayStart, "hours").hours;
        let nextDayMinLead = calendarInfo.minLeadTime;
        nextDayMinLead = timeLeftUntilClose <= 0 ? nextDayMinLead : nextDayMinLead - timeLeftUntilClose;

        //There must be at least nextDayMinLead in the next day for the team to prepare. Anything before that will be disabled.
        if (slotOpenDiff < nextDayMinLead) {
          slotEnabled = false;
        }
      }
    }

    //Check if this slot will surpass the hours of operation
    if (currentTimeSlot.plus(calendarInfo.slotsAllocationDuration) <= dayEnd) {
      timeSlots[currentTimeSlot.toLocaleString(constants.TIME_SIMPLE)] = slotEnabled ? currentTimeSlot : null;
    }

    currentTimeSlot = currentTimeSlot.plus(calendarInfo.slotsIntervalDuration); //Next time slot
  }

  //If a data was selected then check the slots. Otherwise just return a placeholder
  timeSlots = selectedDateInfo
    ? checkTimeSlots(
        dayStart.toISODate(),
        selectedDateInfo.blockedTimes,
        selectedDateInfo.meetings,
        timeSlots,
        calendarInfo,
        availableTimes
      )
    : timeSlots;

  return timeSlots; //Assign time slots to day
};
